import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../utils/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const initialAdministratorForm = { email: '', reason: '' };
const initialAdministratorFormErrors = { email: '', reason: '' };
const initialCustomerForm = { email: '', plan: 'pro', reason: '', expires_at: '' };
const RECENT_AUTH_ERROR = 'Recent password and MFA authentication is required';
const REAUTH_RETURN_PATH_KEY = 'black_peak_reauth_return_path';
const PROTECTED_DELETE_EMAIL = 'asterix1md@gmail.com';

const formatApiError = (detail, fallback) => {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail.map((item) => item?.msg).filter(Boolean);
    if (messages.length) return messages.join(' ');
  }
  return fallback;
};

function AdminAccountManagement({ setIsAuthenticated = () => {} }) {
  const [accounts, setAccounts] = useState([]);
  const [currentAdminId, setCurrentAdminId] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invitationUrl, setInvitationUrl] = useState('');
  const [administratorForm, setAdministratorForm] = useState(initialAdministratorForm);
  const [administratorFormErrors, setAdministratorFormErrors] = useState(initialAdministratorFormErrors);
  const [administratorConfirmation, setAdministratorConfirmation] = useState(null);
  const [administratorFeedback, setAdministratorFeedback] = useState({ type: '', message: '', requiresReauthentication: false });
  const [administratorSubmitting, setAdministratorSubmitting] = useState(false);
  const administratorFeedbackRef = useRef(null);
  const administratorDialogRef = useRef(null);
  const administratorCancelRef = useRef(null);
  const administratorTriggerRef = useRef(null);
  const administratorRequestInFlightRef = useRef(false);
  const restoreAdministratorTriggerFocusRef = useRef(false);
  const [customerForm, setCustomerForm] = useState(initialCustomerForm);
  const [grantConfirmation, setGrantConfirmation] = useState(null);
  const [grantFeedback, setGrantFeedback] = useState({ type: '', message: '', requiresReauthentication: false });
  const [grantSubmitting, setGrantSubmitting] = useState(false);
  const grantFeedbackRef = useRef(null);
  const grantDialogRef = useRef(null);
  const grantCancelRef = useRef(null);
  const grantTriggerRef = useRef(null);
  const grantRequestInFlightRef = useRef(false);
  const restoreGrantTriggerFocusRef = useRef(false);
  const [revokeConfirmation, setRevokeConfirmation] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeReasonError, setRevokeReasonError] = useState('');
  const [revokeFeedback, setRevokeFeedback] = useState({ type: '', message: '', requiresReauthentication: false });
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);
  const revokeFeedbackRef = useRef(null);
  const revokeDialogRef = useRef(null);
  const revokeReasonRef = useRef(null);
  const revokeTriggerRef = useRef(null);
  const revokeRequestInFlightRef = useRef(false);
  const restoreRevokeTriggerFocusRef = useRef(false);
  const [roleConfirmation, setRoleConfirmation] = useState(null);
  const [roleReason, setRoleReason] = useState('');
  const [roleReasonError, setRoleReasonError] = useState('');
  const [roleFeedback, setRoleFeedback] = useState({ type: '', message: '', requiresReauthentication: false });
  const [roleSubmitting, setRoleSubmitting] = useState(false);
  const roleFeedbackRef = useRef(null);
  const roleDialogRef = useRef(null);
  const roleReasonRef = useRef(null);
  const roleTriggerRef = useRef(null);
  const roleRequestInFlightRef = useRef(false);
  const restoreRoleTriggerFocusRef = useRef(false);
  const [suspensionConfirmation, setSuspensionConfirmation] = useState(null);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [suspensionReasonError, setSuspensionReasonError] = useState('');
  const [suspensionFeedback, setSuspensionFeedback] = useState({ type: '', message: '', requiresReauthentication: false });
  const [suspensionSubmitting, setSuspensionSubmitting] = useState(false);
  const suspensionFeedbackRef = useRef(null);
  const suspensionDialogRef = useRef(null);
  const suspensionReasonRef = useRef(null);
  const suspensionTriggerRef = useRef(null);
  const suspensionRequestInFlightRef = useRef(false);
  const restoreSuspensionTriggerFocusRef = useRef(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [deleteFeedback, setDeleteFeedback] = useState({ type: '', message: '', requiresReauthentication: false });
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const deleteFeedbackRef = useRef(null);
  const deleteDialogRef = useRef(null);
  const deleteTriggerRef = useRef(null);
  const deleteRequestInFlightRef = useRef(false);
  const restoreDeleteTriggerFocusRef = useRef(false);

  const apiRequest = useCallback(async (path, options = {}) => {
    const response = await apiFetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
    let data = {};
    try {
      data = await response.json();
    } catch (parseError) {
      if (response.ok) throw new Error('The server returned an unreadable response');
    }
    if (!response.ok) {
      const requestError = new Error(formatApiError(data.detail, `Account operation failed (${response.status})`));
      requestError.status = response.status;
      requestError.detail = data.detail;
      throw requestError;
    }
    return data;
  }, []);

  const loadAccounts = useCallback(async (search = '', { showLoading = true } = {}) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      const data = await apiRequest(`/admin/accounts?q=${encodeURIComponent(search)}`);
      setAccounts(data.accounts || []);
      setCurrentAdminId(data.current_admin_id || null);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [apiRequest]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  useEffect(() => {
    if (administratorFeedback.message && administratorFeedback.type !== 'cancelled') administratorFeedbackRef.current?.focus();
  }, [administratorFeedback]);

  useEffect(() => {
    if (grantFeedback.message && grantFeedback.type !== 'cancelled') grantFeedbackRef.current?.focus();
  }, [grantFeedback]);

  useEffect(() => {
    if (revokeFeedback.message && revokeFeedback.type !== 'cancelled') revokeFeedbackRef.current?.focus();
  }, [revokeFeedback]);

  useEffect(() => {
    if (roleFeedback.message && roleFeedback.type !== 'cancelled') roleFeedbackRef.current?.focus();
  }, [roleFeedback]);

  useEffect(() => {
    if (suspensionFeedback.message && suspensionFeedback.type !== 'cancelled') suspensionFeedbackRef.current?.focus();
  }, [suspensionFeedback]);

  useEffect(() => {
    if (deleteFeedback.message && deleteFeedback.type !== 'cancelled') deleteFeedbackRef.current?.focus();
  }, [deleteFeedback]);

  const showResult = (message, data = {}, { showGlobalMessage = true } = {}) => {
    setError('');
    if (showGlobalMessage) setSuccess(message);
    setInvitationUrl(data.invitation_url || '');
    if (data.account) {
      setAccounts((currentAccounts) => {
        const existing = currentAccounts.some((account) => account.id === data.account.id);
        return existing
          ? currentAccounts.map((account) => account.id === data.account.id ? data.account : account)
          : [data.account, ...currentAccounts];
      });
    }
    void loadAccounts(query, { showLoading: false });
  };

  const validateAdministratorForm = () => {
    const email = administratorForm.email.trim();
    const reason = administratorForm.reason.trim();
    const errors = { ...initialAdministratorFormErrors };
    if (!email) {
      errors.email = 'Enter an administrator email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Enter a valid administrator email address.';
    }
    if (reason.length < 3 || reason.length > 1000) {
      errors.reason = 'Reason must be between 3 and 1000 characters.';
    }
    setAdministratorFormErrors(errors);
    if (errors.email || errors.reason) return null;
    return {
      email,
      reason,
      confirmation: 'CREATE ADMINISTRATOR',
    };
  };

  const findKnownAccountByEmail = (email) => accounts.find(
    (account) => account.email?.toLowerCase() === email.toLowerCase(),
  );

  const requestAdministratorConfirmation = (event) => {
    event.preventDefault();
    if (administratorRequestInFlightRef.current || administratorConfirmation) return;
    setError('');
    setSuccess('');
    setInvitationUrl('');
    setAdministratorFeedback({ type: '', message: '', requiresReauthentication: false });
    const payload = validateAdministratorForm();
    if (!payload) return;
    const knownAccount = findKnownAccountByEmail(payload.email);
    setAdministratorConfirmation({
      payload,
      mode: knownAccount?.is_admin ? 'already_admin' : knownAccount ? 'existing_customer' : 'new_account',
      knownAccount: knownAccount || null,
    });
  };

  const cancelAdministratorConfirmation = useCallback(() => {
    if (administratorRequestInFlightRef.current) return;
    restoreAdministratorTriggerFocusRef.current = true;
    setAdministratorConfirmation(null);
    setAdministratorFeedback({
      type: 'cancelled',
      message: 'Administrator creation canceled. No request was sent.',
      requiresReauthentication: false,
    });
  }, []);

  const confirmAdministrator = async () => {
    if (!administratorConfirmation || administratorRequestInFlightRef.current) return;
    administratorRequestInFlightRef.current = true;
    setAdministratorSubmitting(true);
    setAdministratorFeedback({ type: '', message: '', requiresReauthentication: false });
    try {
      const data = await apiRequest('/admin/accounts/administrators', {
        method: 'POST',
        body: JSON.stringify(administratorConfirmation.payload),
      });
      setAdministratorConfirmation(null);
      setAdministratorForm(initialAdministratorForm);
      setAdministratorFormErrors(initialAdministratorFormErrors);
      const message = data.promoted
        ? 'Existing customer converted to administrator.'
        : 'Administrator invitation created.';
      setAdministratorFeedback({ type: 'success', message, requiresReauthentication: false });
      showResult(message, data, { showGlobalMessage: false });
    } catch (requestError) {
      setAdministratorConfirmation(null);
      const requiresReauthentication = requestError.status === 401 && requestError.message === RECENT_AUTH_ERROR;
      const message = requiresReauthentication
        ? 'Your administrator verification expired. Sign in again with your password and MFA, then retry administrator creation. No account changes were made.'
        : requestError.message;
      setAdministratorFeedback({ type: 'error', message, requiresReauthentication });
    } finally {
      administratorRequestInFlightRef.current = false;
      setAdministratorSubmitting(false);
    }
  };

  useEffect(() => {
    if (!administratorConfirmation) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    administratorCancelRef.current?.focus();

    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!administratorRequestInFlightRef.current) {
          event.preventDefault();
          cancelAdministratorConfirmation();
        }
        return;
      }
      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        administratorDialogRef.current?.querySelectorAll('button:not(:disabled)') || [],
      );
      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleDialogKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDialogKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [administratorConfirmation, cancelAdministratorConfirmation]);

  useEffect(() => {
    if (!administratorConfirmation && restoreAdministratorTriggerFocusRef.current) {
      restoreAdministratorTriggerFocusRef.current = false;
      administratorTriggerRef.current?.focus();
    }
  }, [administratorConfirmation]);

  const requestGrantConfirmation = (event) => {
    event.preventDefault();
    if (grantRequestInFlightRef.current || grantConfirmation) return;
    setError('');
    setSuccess('');
    setGrantFeedback({ type: '', message: '', requiresReauthentication: false });
    try {
      let expiration = null;
      let expirationLabel = 'No expiration';
      if (customerForm.expires_at) {
        const parsedExpiration = new Date(customerForm.expires_at);
        if (Number.isNaN(parsedExpiration.getTime())) throw new Error('Enter a valid expiration date and time.');
        expiration = parsedExpiration.toISOString();
        expirationLabel = parsedExpiration.toLocaleString();
      }
      setGrantConfirmation({
        payload: {
          ...customerForm,
          expires_at: expiration,
          confirmation: 'GRANT COMPLIMENTARY ACCESS',
        },
        planLabel: customerForm.plan === 'premium' ? 'Premium' : 'Pro',
        expirationLabel,
      });
    } catch (requestError) {
      setGrantFeedback({ type: 'error', message: requestError.message, requiresReauthentication: false });
    }
  };

  const cancelGrantConfirmation = useCallback(() => {
    if (grantRequestInFlightRef.current) return;
    restoreGrantTriggerFocusRef.current = true;
    setGrantConfirmation(null);
    setGrantFeedback({
      type: 'cancelled',
      message: 'Complimentary access grant canceled. No request was sent.',
      requiresReauthentication: false,
    });
  }, []);

  const confirmGrant = async () => {
    if (!grantConfirmation || grantRequestInFlightRef.current) return;
    grantRequestInFlightRef.current = true;
    setGrantSubmitting(true);
    setGrantFeedback({ type: '', message: '', requiresReauthentication: false });
    try {
      const data = await apiRequest('/admin/accounts/customers', {
        method: 'POST',
        body: JSON.stringify(grantConfirmation.payload),
      });
      setGrantConfirmation(null);
      setCustomerForm(initialCustomerForm);
      const message = data.created ? 'Customer invitation and complimentary access created.' : 'Complimentary access updated.';
      setGrantFeedback({ type: 'success', message, requiresReauthentication: false });
      showResult(message, data, { showGlobalMessage: false });
    } catch (requestError) {
      setGrantConfirmation(null);
      const requiresReauthentication = requestError.status === 401 && requestError.message === RECENT_AUTH_ERROR;
      const message = requiresReauthentication
        ? 'Your administrator verification expired. Sign in again with your password and MFA, then retry the grant. No account changes were made.'
        : requestError.message;
      setGrantFeedback({ type: 'error', message, requiresReauthentication });
    } finally {
      grantRequestInFlightRef.current = false;
      setGrantSubmitting(false);
    }
  };

  useEffect(() => {
    if (!grantConfirmation) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    grantCancelRef.current?.focus();

    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!grantRequestInFlightRef.current) {
          event.preventDefault();
          cancelGrantConfirmation();
        }
        return;
      }
      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        grantDialogRef.current?.querySelectorAll('button:not(:disabled)') || [],
      );
      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleDialogKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDialogKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [cancelGrantConfirmation, grantConfirmation]);

  useEffect(() => {
    if (!grantConfirmation && restoreGrantTriggerFocusRef.current) {
      restoreGrantTriggerFocusRef.current = false;
      grantTriggerRef.current?.focus();
    }
  }, [grantConfirmation]);

  const reauthenticateAdministrator = () => {
    sessionStorage.setItem(REAUTH_RETURN_PATH_KEY, '/admin/account-management');
    setIsAuthenticated(false);
    // HttpOnly cookies cannot be cleared by JavaScript; revoke the current
    // server session before sending the administrator back through sign-in.
    void apiRequest('/logout', { method: 'POST' }).catch(() => {});
  };

  const requestRevokeConfirmation = (account, trigger) => {
    if (revokeRequestInFlightRef.current || revokeConfirmation) return;
    revokeTriggerRef.current = trigger;
    setError('');
    setSuccess('');
    setRevokeReason('');
    setRevokeReasonError('');
    setRevokeFeedback({ type: '', message: '', requiresReauthentication: false });
    setRevokeConfirmation({
      accountId: account.id,
      email: account.email,
      planLabel: account.complimentary_grant?.plan || account.effective_plan,
      expirationLabel: formatDate(account.complimentary_grant?.expires_at),
    });
  };

  const cancelRevokeConfirmation = useCallback(() => {
    if (!revokeConfirmation || revokeRequestInFlightRef.current) return;
    restoreRevokeTriggerFocusRef.current = true;
    setRevokeConfirmation(null);
    setRevokeReason('');
    setRevokeReasonError('');
    setRevokeFeedback({
      type: 'cancelled',
      message: 'Complimentary access revocation canceled. No request was sent.',
      requiresReauthentication: false,
    });
  }, [revokeConfirmation]);

  const confirmRevoke = async (event) => {
    event.preventDefault();
    if (!revokeConfirmation || revokeRequestInFlightRef.current) return;

    const normalizedReason = revokeReason.trim();
    if (normalizedReason.length < 3 || normalizedReason.length > 1000) {
      setRevokeReasonError('Reason must be between 3 and 1000 characters.');
      revokeReasonRef.current?.focus();
      return;
    }

    const confirmation = revokeConfirmation;
    revokeRequestInFlightRef.current = true;
    setRevokeSubmitting(true);
    setRevokeReasonError('');
    setRevokeFeedback({ type: '', message: '', requiresReauthentication: false });
    try {
      const data = await apiRequest(`/admin/accounts/${confirmation.accountId}/complimentary-access/revoke`, {
        method: 'POST',
        body: JSON.stringify({
          reason: normalizedReason,
          confirmation: 'REVOKE COMPLIMENTARY ACCESS',
        }),
      });
      setRevokeConfirmation(null);
      setRevokeReason('');
      const message = 'Complimentary access revoked.';
      setRevokeFeedback({ type: 'success', message, requiresReauthentication: false });
      showResult(message, data, { showGlobalMessage: false });
    } catch (requestError) {
      setRevokeConfirmation(null);
      setRevokeReason('');
      const requiresReauthentication = requestError.status === 401 && requestError.message === RECENT_AUTH_ERROR;
      const message = requiresReauthentication
        ? 'Your administrator verification expired. Sign in again with your password and MFA, then retry the revocation. No account changes were made.'
        : requestError.message;
      setRevokeFeedback({ type: 'error', message, requiresReauthentication });
    } finally {
      revokeRequestInFlightRef.current = false;
      setRevokeSubmitting(false);
    }
  };

  useEffect(() => {
    if (!revokeConfirmation) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    revokeReasonRef.current?.focus();

    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!revokeRequestInFlightRef.current) {
          event.preventDefault();
          cancelRevokeConfirmation();
        }
        return;
      }
      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        revokeDialogRef.current?.querySelectorAll('textarea:not(:disabled), button:not(:disabled)') || [],
      );
      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleDialogKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDialogKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [cancelRevokeConfirmation, revokeConfirmation]);

  useEffect(() => {
    if (!revokeConfirmation && restoreRevokeTriggerFocusRef.current) {
      restoreRevokeTriggerFocusRef.current = false;
      revokeTriggerRef.current?.focus();
    }
  }, [revokeConfirmation]);

  const requestRoleConfirmation = (account, trigger) => {
    if (!account.is_admin || roleRequestInFlightRef.current || roleConfirmation) return;
    roleTriggerRef.current = trigger;
    setError('');
    setSuccess('');
    setRoleReason('');
    setRoleReasonError('');
    setRoleFeedback({ type: '', message: '', requiresReauthentication: false });
    setRoleConfirmation({
      accountId: account.id,
      email: account.email,
      planLabel: account.effective_plan,
      accessSource: account.access_source,
      grantLabel: account.complimentary_grant
        ? `${account.complimentary_grant.plan} · ${formatDate(account.complimentary_grant.expires_at)}`
        : 'No active complimentary grant',
    });
  };

  const cancelRoleConfirmation = useCallback(() => {
    if (!roleConfirmation || roleRequestInFlightRef.current) return;
    restoreRoleTriggerFocusRef.current = true;
    setRoleConfirmation(null);
    setRoleReason('');
    setRoleReasonError('');
    setRoleFeedback({
      type: 'cancelled',
      message: 'Administrator role removal canceled. No request was sent.',
      requiresReauthentication: false,
    });
  }, [roleConfirmation]);

  const confirmRoleRemoval = async (event) => {
    event.preventDefault();
    if (!roleConfirmation || roleRequestInFlightRef.current) return;

    const normalizedReason = roleReason.trim();
    if (normalizedReason.length < 3 || normalizedReason.length > 1000) {
      setRoleReasonError('Reason must be between 3 and 1000 characters.');
      roleReasonRef.current?.focus();
      return;
    }

    const confirmation = roleConfirmation;
    roleRequestInFlightRef.current = true;
    setRoleSubmitting(true);
    setRoleReasonError('');
    setRoleFeedback({ type: '', message: '', requiresReauthentication: false });
    try {
      const data = await apiRequest(`/admin/accounts/${confirmation.accountId}/role`, {
        method: 'POST',
        body: JSON.stringify({
          is_admin: false,
          reason: normalizedReason,
          confirmation: 'CHANGE ADMINISTRATOR ROLE',
        }),
      });
      setRoleConfirmation(null);
      setRoleReason('');
      const message = 'Administrator role removed.';
      setRoleFeedback({ type: 'success', message, requiresReauthentication: false });
      showResult(message, data, { showGlobalMessage: false });
    } catch (requestError) {
      setRoleConfirmation(null);
      setRoleReason('');
      const requiresReauthentication = requestError.status === 401 && requestError.message === RECENT_AUTH_ERROR;
      const message = requiresReauthentication
        ? 'Your administrator verification expired. Sign in again with your password and MFA, then retry removing the administrator role. No account changes were made.'
        : requestError.message;
      setRoleFeedback({ type: 'error', message, requiresReauthentication });
    } finally {
      roleRequestInFlightRef.current = false;
      setRoleSubmitting(false);
    }
  };

  useEffect(() => {
    if (!roleConfirmation) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    roleReasonRef.current?.focus();

    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!roleRequestInFlightRef.current) {
          event.preventDefault();
          cancelRoleConfirmation();
        }
        return;
      }
      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        roleDialogRef.current?.querySelectorAll('textarea:not(:disabled), button:not(:disabled)') || [],
      );
      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleDialogKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDialogKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [cancelRoleConfirmation, roleConfirmation]);

  useEffect(() => {
    if (!roleConfirmation && restoreRoleTriggerFocusRef.current) {
      restoreRoleTriggerFocusRef.current = false;
      roleTriggerRef.current?.focus();
    }
  }, [roleConfirmation]);

  const requestDeleteConfirmation = (account, trigger) => {
    if (account.email?.trim().toLowerCase() === PROTECTED_DELETE_EMAIL || deleteRequestInFlightRef.current || deleteConfirmation) return;
    deleteTriggerRef.current = trigger;
    setError('');
    setSuccess('');
    setDeleteFeedback({ type: '', message: '', requiresReauthentication: false });
    setDeleteConfirmation({ accountId: account.id, email: account.email });
  };

  const cancelDeleteConfirmation = useCallback(() => {
    if (!deleteConfirmation || deleteRequestInFlightRef.current) return;
    restoreDeleteTriggerFocusRef.current = true;
    setDeleteConfirmation(null);
    setDeleteFeedback({
      type: 'cancelled',
      message: 'Permanent account deletion canceled. No request was sent.',
      requiresReauthentication: false,
    });
  }, [deleteConfirmation]);

  const confirmDelete = async (event) => {
    event.preventDefault();
    if (!deleteConfirmation || deleteRequestInFlightRef.current) return;

    const confirmation = deleteConfirmation;
    deleteRequestInFlightRef.current = true;
    setDeleteSubmitting(true);
    setDeleteFeedback({ type: '', message: '', requiresReauthentication: false });
    try {
      await apiRequest(`/admin/accounts/${confirmation.accountId}`, {
        method: 'DELETE',
        body: JSON.stringify({
          email: confirmation.email,
          confirmation: 'DELETE ACCOUNT PERMANENTLY',
        }),
      });
      setDeleteConfirmation(null);
      setDeleteFeedback({ type: 'success', message: 'Account deleted permanently.', requiresReauthentication: false });
      void loadAccounts(query, { showLoading: false });
    } catch (requestError) {
      setDeleteConfirmation(null);
      const requiresReauthentication = requestError.status === 401 && requestError.message === RECENT_AUTH_ERROR;
      const message = requiresReauthentication
        ? 'Your administrator verification expired. Sign in again with your password and MFA, then retry the deletion. No account changes were made.'
        : requestError.message;
      setDeleteFeedback({ type: 'error', message, requiresReauthentication });
    } finally {
      deleteRequestInFlightRef.current = false;
      setDeleteSubmitting(false);
    }
  };

  useEffect(() => {
    if (!deleteConfirmation && restoreDeleteTriggerFocusRef.current) {
      restoreDeleteTriggerFocusRef.current = false;
      deleteTriggerRef.current?.focus();
    }
  }, [deleteConfirmation]);

  const requestSuspensionConfirmation = (account, trigger) => {
    if (suspensionRequestInFlightRef.current || suspensionConfirmation) return;
    const suspended = account.account_status !== 'suspended';
    suspensionTriggerRef.current = trigger;
    setError('');
    setSuccess('');
    setSuspensionReason('');
    setSuspensionReasonError('');
    setSuspensionFeedback({ type: '', message: '', requiresReauthentication: false });
    setSuspensionConfirmation({
      accountId: account.id,
      email: account.email,
      suspended,
      actionLabel: suspended ? 'Suspend' : 'Reactivate',
      actionNoun: suspended ? 'suspension' : 'reactivation',
    });
  };

  const cancelSuspensionConfirmation = useCallback(() => {
    if (!suspensionConfirmation || suspensionRequestInFlightRef.current) return;
    const { actionNoun } = suspensionConfirmation;
    restoreSuspensionTriggerFocusRef.current = true;
    setSuspensionConfirmation(null);
    setSuspensionReason('');
    setSuspensionReasonError('');
    setSuspensionFeedback({
      type: 'cancelled',
      message: `Account ${actionNoun} canceled. No request was sent.`,
      requiresReauthentication: false,
    });
  }, [suspensionConfirmation]);

  const confirmSuspension = async (event) => {
    event.preventDefault();
    if (!suspensionConfirmation || suspensionRequestInFlightRef.current) return;

    const normalizedReason = suspensionReason.trim();
    if (normalizedReason.length < 3 || normalizedReason.length > 1000) {
      setSuspensionReasonError('Reason must be between 3 and 1000 characters.');
      suspensionReasonRef.current?.focus();
      return;
    }

    const confirmation = suspensionConfirmation;
    suspensionRequestInFlightRef.current = true;
    setSuspensionSubmitting(true);
    setSuspensionReasonError('');
    setSuspensionFeedback({ type: '', message: '', requiresReauthentication: false });
    try {
      const data = await apiRequest(`/admin/accounts/${confirmation.accountId}/suspension`, {
        method: 'POST',
        body: JSON.stringify({
          suspended: confirmation.suspended,
          reason: normalizedReason,
          confirmation: 'CHANGE ACCOUNT STATUS',
        }),
      });
      setSuspensionConfirmation(null);
      setSuspensionReason('');
      const message = confirmation.suspended ? 'Account suspended.' : 'Account reactivated.';
      setSuspensionFeedback({ type: 'success', message, requiresReauthentication: false });
      showResult(message, data, { showGlobalMessage: false });
    } catch (requestError) {
      setSuspensionConfirmation(null);
      setSuspensionReason('');
      const requiresReauthentication = requestError.status === 401 && requestError.message === RECENT_AUTH_ERROR;
      const message = requiresReauthentication
        ? 'Your administrator verification expired. Sign in again with your password and MFA, then retry the account status change. No account changes were made.'
        : requestError.message;
      setSuspensionFeedback({ type: 'error', message, requiresReauthentication });
    } finally {
      suspensionRequestInFlightRef.current = false;
      setSuspensionSubmitting(false);
    }
  };

  useEffect(() => {
    if (!suspensionConfirmation) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    suspensionReasonRef.current?.focus();

    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!suspensionRequestInFlightRef.current) {
          event.preventDefault();
          cancelSuspensionConfirmation();
        }
        return;
      }
      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        suspensionDialogRef.current?.querySelectorAll('textarea:not(:disabled), button:not(:disabled)') || [],
      );
      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleDialogKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDialogKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [cancelSuspensionConfirmation, suspensionConfirmation]);

  useEffect(() => {
    if (!suspensionConfirmation && restoreSuspensionTriggerFocusRef.current) {
      restoreSuspensionTriggerFocusRef.current = false;
      suspensionTriggerRef.current?.focus();
    }
  }, [suspensionConfirmation]);

  const formatDate = (value) => value ? new Date(value).toLocaleDateString() : 'No expiration';

  return (
    <div className="authenticated-page admin-account-page">
      <div className="authenticated-page-content">
        <header className="admin-quality-header">
          <h1 className="admin-quality-title">Account Management</h1>
          <p className="admin-quality-subtitle">Create invited accounts, manage roles, and grant audited complimentary access.</p>
        </header>

        {administratorConfirmation && (
          <div
            className="admin-account-confirmation-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) cancelAdministratorConfirmation();
            }}
          >
            <div
              ref={administratorDialogRef}
              className="admin-account-confirmation-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="administrator-confirmation-title"
              aria-describedby="administrator-confirmation-description"
              aria-busy={administratorSubmitting}
            >
              <h2 id="administrator-confirmation-title">Confirm Create Administrator</h2>
              <p id="administrator-confirmation-description">
                {administratorConfirmation.mode === 'existing_customer'
                  ? 'This will convert the existing customer account to administrator. Its password, plan, Stripe billing details, MFA setup, and account data are retained. Existing sessions are revoked, and the user must sign in again; if MFA is not enrolled, the next login will require setup.'
                  : administratorConfirmation.mode === 'already_admin'
                    ? 'This email already appears to belong to an administrator. Confirming will ask the server to verify the current account state and reject duplicate administrator creation if it is still an administrator.'
                    : 'This creates a new administrator invitation if no account exists. If this email belongs to an existing customer, the server will convert that account to administrator instead of creating a duplicate.'}
              </p>
              <dl className="admin-account-confirmation-details">
                <div>
                  <dt>Email</dt>
                  <dd>{administratorConfirmation.payload.email}</dd>
                </div>
                <div>
                  <dt>Role</dt>
                  <dd>Administrator</dd>
                </div>
                {administratorConfirmation.knownAccount && (
                  <>
                    <div>
                      <dt>Current account</dt>
                      <dd>{administratorConfirmation.knownAccount.role}</dd>
                    </div>
                    <div>
                      <dt>Current plan</dt>
                      <dd>{administratorConfirmation.knownAccount.effective_plan}</dd>
                    </div>
                  </>
                )}
                <div className="admin-account-confirmation-reason">
                  <dt>Reason</dt>
                  <dd>{administratorConfirmation.payload.reason}</dd>
                </div>
              </dl>
              <div className="admin-account-confirmation-actions">
                <button
                  ref={administratorCancelRef}
                  className="admin-quality-btn-secondary"
                  type="button"
                  onClick={cancelAdministratorConfirmation}
                  disabled={administratorSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="admin-quality-btn-primary"
                  type="button"
                  onClick={confirmAdministrator}
                  disabled={administratorSubmitting}
                >
                  {administratorSubmitting ? 'Creating Administrator…' : 'Confirm Create Administrator'}
                </button>
              </div>
            </div>
          </div>
        )}

        {grantConfirmation && (
          <div className="admin-account-confirmation-backdrop">
            <div
              ref={grantDialogRef}
              className="admin-account-confirmation-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="grant-confirmation-title"
              aria-describedby="grant-confirmation-description"
              aria-busy={grantSubmitting}
            >
              <h2 id="grant-confirmation-title">Confirm Complimentary Access</h2>
              <p id="grant-confirmation-description">
                Review the grant details before giving this customer complimentary access.
              </p>
              <dl className="admin-account-confirmation-details">
                <div>
                  <dt>Customer</dt>
                  <dd>{grantConfirmation.payload.email}</dd>
                </div>
                <div>
                  <dt>Plan</dt>
                  <dd>{grantConfirmation.planLabel}</dd>
                </div>
                <div>
                  <dt>Expiration</dt>
                  <dd>{grantConfirmation.expirationLabel}</dd>
                </div>
                <div className="admin-account-confirmation-reason">
                  <dt>Reason</dt>
                  <dd>{grantConfirmation.payload.reason}</dd>
                </div>
              </dl>
              <div className="admin-account-confirmation-actions">
                <button
                  ref={grantCancelRef}
                  className="admin-quality-btn-secondary"
                  type="button"
                  onClick={cancelGrantConfirmation}
                  disabled={grantSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="admin-quality-btn-primary"
                  type="button"
                  onClick={confirmGrant}
                  disabled={grantSubmitting}
                >
                  {grantSubmitting ? 'Granting Complimentary Access…' : 'Confirm Grant'}
                </button>
              </div>
            </div>
          </div>
        )}

        {revokeConfirmation && (
          <div
            className="admin-account-confirmation-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) cancelRevokeConfirmation();
            }}
          >
            <form
              ref={revokeDialogRef}
              className="admin-account-confirmation-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="revoke-confirmation-title"
              aria-describedby="revoke-confirmation-description"
              aria-busy={revokeSubmitting}
              onSubmit={confirmRevoke}
              noValidate
            >
              <h2 id="revoke-confirmation-title">Confirm Revoke Complimentary Access</h2>
              <p id="revoke-confirmation-description">
                Review the account and enter the administrative reason for revoking this complimentary grant.
              </p>
              <dl className="admin-account-confirmation-details">
                <div>
                  <dt>Account</dt>
                  <dd>{revokeConfirmation.email}</dd>
                </div>
                <div>
                  <dt>Plan</dt>
                  <dd>{revokeConfirmation.planLabel}</dd>
                </div>
                <div>
                  <dt>Expiration</dt>
                  <dd>{revokeConfirmation.expirationLabel}</dd>
                </div>
                <div>
                  <dt>Action</dt>
                  <dd>Revoke complimentary access</dd>
                </div>
              </dl>
              <label className="admin-quality-field admin-account-confirmation-field">
                <span className="admin-quality-label">Reason (3–1000 characters)</span>
                <textarea
                  ref={revokeReasonRef}
                  className="admin-quality-textarea"
                  required
                  minLength="3"
                  maxLength="1000"
                  value={revokeReason}
                  onChange={(event) => {
                    setRevokeReason(event.target.value);
                    if (revokeReasonError) setRevokeReasonError('');
                  }}
                  aria-invalid={Boolean(revokeReasonError)}
                  aria-describedby={revokeReasonError ? 'revoke-reason-error' : undefined}
                  disabled={revokeSubmitting}
                />
                {revokeReasonError && (
                  <span id="revoke-reason-error" className="admin-account-field-error" role="alert">
                    {revokeReasonError}
                  </span>
                )}
              </label>
              <div className="admin-account-confirmation-actions">
                <button
                  className="admin-quality-btn-secondary"
                  type="button"
                  onClick={cancelRevokeConfirmation}
                  disabled={revokeSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="admin-quality-btn-primary"
                  type="submit"
                  disabled={revokeSubmitting}
                >
                  {revokeSubmitting ? 'Revoking Complimentary Access…' : 'Confirm Revoke'}
                </button>
              </div>
            </form>
          </div>
        )}

        {roleConfirmation && (
          <div
            className="admin-account-confirmation-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) cancelRoleConfirmation();
            }}
          >
            <form
              ref={roleDialogRef}
              className="admin-account-confirmation-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="role-confirmation-title"
              aria-describedby="role-confirmation-description"
              aria-busy={roleSubmitting}
              onSubmit={confirmRoleRemoval}
              noValidate
            >
              <h2 id="role-confirmation-title">Confirm Remove Administrator</h2>
              <p id="role-confirmation-description">
                This removes administrator privileges from the target account. The account becomes a normal customer.
                Password, plan, Stripe billing details, complimentary grants, email-code MFA and account data are retained.
                Existing sessions are revoked, and the user must sign in again.
              </p>
              <dl className="admin-account-confirmation-details">
                <div>
                  <dt>Account</dt>
                  <dd>{roleConfirmation.email}</dd>
                </div>
                <div>
                  <dt>Current plan</dt>
                  <dd>{roleConfirmation.planLabel}</dd>
                </div>
                <div>
                  <dt>Access source</dt>
                  <dd>{roleConfirmation.accessSource}</dd>
                </div>
                <div>
                  <dt>Complimentary grant</dt>
                  <dd>{roleConfirmation.grantLabel}</dd>
                </div>
                <div>
                  <dt>Action</dt>
                  <dd>Remove administrator role</dd>
                </div>
              </dl>
              <label className="admin-quality-field admin-account-confirmation-field">
                <span className="admin-quality-label">Reason (3–1000 characters)</span>
                <textarea
                  ref={roleReasonRef}
                  className="admin-quality-textarea"
                  required
                  minLength="3"
                  maxLength="1000"
                  value={roleReason}
                  onChange={(event) => {
                    setRoleReason(event.target.value);
                    if (roleReasonError) setRoleReasonError('');
                  }}
                  aria-invalid={Boolean(roleReasonError)}
                  aria-describedby={roleReasonError ? 'role-reason-error' : undefined}
                  disabled={roleSubmitting}
                />
                {roleReasonError && (
                  <span id="role-reason-error" className="admin-account-field-error" role="alert">
                    {roleReasonError}
                  </span>
                )}
              </label>
              <div className="admin-account-confirmation-actions">
                <button
                  className="admin-quality-btn-secondary"
                  type="button"
                  onClick={cancelRoleConfirmation}
                  disabled={roleSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="admin-quality-btn-primary"
                  type="submit"
                  disabled={roleSubmitting}
                >
                  {roleSubmitting ? 'Removing Administrator…' : 'Confirm Remove Admin'}
                </button>
              </div>
            </form>
          </div>
        )}

        {deleteConfirmation && (
          <div
            className="admin-account-confirmation-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) cancelDeleteConfirmation();
            }}
          >
            <form
              ref={deleteDialogRef}
              className="admin-account-confirmation-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-confirmation-title"
              aria-describedby="delete-confirmation-description"
              aria-busy={deleteSubmitting}
              onSubmit={confirmDelete}
            >
              <h2 id="delete-confirmation-title">Confirm Permanent Account Deletion</h2>
              <p id="delete-confirmation-description">
                This permanently deletes the account and its account-owned data. This cannot be undone.
              </p>
              <dl className="admin-account-confirmation-details">
                <div>
                  <dt>Account</dt>
                  <dd>{deleteConfirmation.email}</dd>
                </div>
                <div>
                  <dt>Action</dt>
                  <dd>Delete permanently</dd>
                </div>
              </dl>
              <div className="admin-account-confirmation-actions">
                <button className="admin-quality-btn-secondary" type="button" onClick={cancelDeleteConfirmation} disabled={deleteSubmitting}>
                  Cancel
                </button>
                <button className="admin-quality-btn-primary" type="submit" disabled={deleteSubmitting}>
                  {deleteSubmitting ? 'Deleting permanently…' : 'Delete permanently'}
                </button>
              </div>
            </form>
          </div>
        )}

        {suspensionConfirmation && (
          <div
            className="admin-account-confirmation-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) cancelSuspensionConfirmation();
            }}
          >
            <form
              ref={suspensionDialogRef}
              className="admin-account-confirmation-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="suspension-confirmation-title"
              aria-describedby="suspension-confirmation-description"
              aria-busy={suspensionSubmitting}
              onSubmit={confirmSuspension}
              noValidate
            >
              <h2 id="suspension-confirmation-title">Confirm Account {suspensionConfirmation.actionNoun === 'suspension' ? 'Suspension' : 'Reactivation'}</h2>
              <p id="suspension-confirmation-description">
                Review the account and enter the administrative reason for this status change.
              </p>
              <dl className="admin-account-confirmation-details">
                <div>
                  <dt>Account</dt>
                  <dd>{suspensionConfirmation.email}</dd>
                </div>
                <div>
                  <dt>Action</dt>
                  <dd>{suspensionConfirmation.actionLabel}</dd>
                </div>
              </dl>
              <label className="admin-quality-field admin-account-confirmation-field">
                <span className="admin-quality-label">Reason (3–1000 characters)</span>
                <textarea
                  ref={suspensionReasonRef}
                  className="admin-quality-textarea"
                  required
                  minLength="3"
                  maxLength="1000"
                  value={suspensionReason}
                  onChange={(event) => {
                    setSuspensionReason(event.target.value);
                    if (suspensionReasonError) setSuspensionReasonError('');
                  }}
                  aria-invalid={Boolean(suspensionReasonError)}
                  aria-describedby={suspensionReasonError ? 'suspension-reason-error' : undefined}
                  disabled={suspensionSubmitting}
                />
                {suspensionReasonError && (
                  <span id="suspension-reason-error" className="admin-account-field-error" role="alert">
                    {suspensionReasonError}
                  </span>
                )}
              </label>
              <div className="admin-account-confirmation-actions">
                <button
                  className="admin-quality-btn-secondary"
                  type="button"
                  onClick={cancelSuspensionConfirmation}
                  disabled={suspensionSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="admin-quality-btn-primary"
                  type="submit"
                  disabled={suspensionSubmitting}
                >
                  {suspensionSubmitting
                    ? `${suspensionConfirmation.actionLabel === 'Suspend' ? 'Suspending' : 'Reactivating'} Account…`
                    : `Confirm ${suspensionConfirmation.actionLabel}`}
                </button>
              </div>
            </form>
          </div>
        )}

        {error && <div className="admin-quality-error" role="alert">{error}</div>}
        {success && <div className="admin-quality-success">{success}</div>}
        {invitationUrl && (
          <div className="admin-account-invitation">
            <strong>One-time password setup link</strong>
            <p>Share this link securely if email delivery was unavailable. It expires in 48 hours.</p>
            <input className="admin-quality-input" readOnly value={invitationUrl} aria-label="Invitation link" />
          </div>
        )}

        <div className="admin-account-form-grid">
          <section className="admin-quality-section">
            <div className="admin-quality-section-header"><h2>Create Administrator</h2></div>
            <form onSubmit={requestAdministratorConfirmation} className="admin-quality-form-grid" noValidate>
              <label className="admin-quality-field admin-quality-field-full">
                <span className="admin-quality-label">Email</span>
                <input
                  className="admin-quality-input"
                  type="email"
                  required
                  value={administratorForm.email}
                  onChange={(event) => {
                    setAdministratorForm({ ...administratorForm, email: event.target.value });
                    if (administratorFormErrors.email) setAdministratorFormErrors({ ...administratorFormErrors, email: '' });
                  }}
                  aria-invalid={Boolean(administratorFormErrors.email)}
                  aria-describedby={administratorFormErrors.email ? 'administrator-email-error' : undefined}
                />
                {administratorFormErrors.email && (
                  <span id="administrator-email-error" className="admin-account-field-error" role="alert">
                    {administratorFormErrors.email}
                  </span>
                )}
              </label>
              <label className="admin-quality-field admin-quality-field-full">
                <span className="admin-quality-label">Reason</span>
                <textarea
                  className="admin-quality-textarea"
                  required
                  minLength="3"
                  maxLength="1000"
                  value={administratorForm.reason}
                  onChange={(event) => {
                    setAdministratorForm({ ...administratorForm, reason: event.target.value });
                    if (administratorFormErrors.reason) setAdministratorFormErrors({ ...administratorFormErrors, reason: '' });
                  }}
                  aria-invalid={Boolean(administratorFormErrors.reason)}
                  aria-describedby={administratorFormErrors.reason ? 'administrator-reason-error' : undefined}
                />
                {administratorFormErrors.reason && (
                  <span id="administrator-reason-error" className="admin-account-field-error" role="alert">
                    {administratorFormErrors.reason}
                  </span>
                )}
              </label>
              {administratorFeedback.message && (
                <div
                  ref={administratorFeedbackRef}
                  className={administratorFeedback.type === 'success'
                    ? 'admin-quality-success admin-quality-field-full'
                    : administratorFeedback.type === 'cancelled'
                      ? 'admin-account-cancelled admin-quality-field-full'
                      : 'admin-quality-error admin-quality-field-full'}
                  role={administratorFeedback.type === 'error' ? 'alert' : 'status'}
                  tabIndex="-1"
                >
                  {administratorFeedback.message}
                  {administratorFeedback.requiresReauthentication && (
                    <div className="admin-account-feedback-action">
                      <button className="admin-quality-btn-secondary" type="button" onClick={reauthenticateAdministrator}>Sign In Again</button>
                    </div>
                  )}
                </div>
              )}
              <button
                ref={administratorTriggerRef}
                className="admin-quality-btn-primary"
                type="submit"
                disabled={administratorSubmitting || Boolean(administratorConfirmation)}
                aria-haspopup="dialog"
              >
                {administratorSubmitting ? 'Creating Administrator…' : 'Create Administrator'}
              </button>
            </form>
          </section>

          <section className="admin-quality-section">
            <div className="admin-quality-section-header"><h2>Grant Complimentary Access</h2></div>
            <form onSubmit={requestGrantConfirmation} className="admin-quality-form-grid">
              <label className="admin-quality-field">
                <span className="admin-quality-label">Customer email</span>
                <input className="admin-quality-input" type="email" required value={customerForm.email} onChange={(event) => setCustomerForm({ ...customerForm, email: event.target.value })} />
              </label>
              <label className="admin-quality-field">
                <span className="admin-quality-label">Plan</span>
                <select className="admin-quality-select" value={customerForm.plan} onChange={(event) => setCustomerForm({ ...customerForm, plan: event.target.value })}>
                  <option value="pro">Pro</option><option value="premium">Premium</option>
                </select>
              </label>
              <label className="admin-quality-field">
                <span className="admin-quality-label">Optional expiration</span>
                <input className="admin-quality-input" type="datetime-local" value={customerForm.expires_at} onChange={(event) => setCustomerForm({ ...customerForm, expires_at: event.target.value })} />
              </label>
              <label className="admin-quality-field">
                <span className="admin-quality-label">Reason</span>
                <input className="admin-quality-input" required minLength="3" maxLength="1000" value={customerForm.reason} onChange={(event) => setCustomerForm({ ...customerForm, reason: event.target.value })} />
              </label>
              {grantFeedback.message && (
                <div
                  ref={grantFeedbackRef}
                  className={grantFeedback.type === 'success'
                    ? 'admin-quality-success admin-quality-field-full'
                    : grantFeedback.type === 'cancelled'
                      ? 'admin-account-cancelled admin-quality-field-full'
                      : 'admin-quality-error admin-quality-field-full'}
                  role={grantFeedback.type === 'error' ? 'alert' : 'status'}
                  tabIndex="-1"
                >
                  {grantFeedback.message}
                  {grantFeedback.requiresReauthentication && (
                    <div className="admin-account-feedback-action">
                      <button className="admin-quality-btn-secondary" type="button" onClick={reauthenticateAdministrator}>Sign In Again</button>
                    </div>
                  )}
                </div>
              )}
              <button
                ref={grantTriggerRef}
                className="admin-quality-btn-primary"
                type="submit"
                disabled={grantSubmitting || Boolean(grantConfirmation)}
                aria-haspopup="dialog"
              >
                {grantSubmitting ? 'Granting Complimentary Access…' : 'Grant Complimentary Access'}
              </button>
            </form>
          </section>
        </div>

        <section className="admin-quality-section">
          <div className="admin-account-list-header">
            <div className="admin-quality-section-header"><h2>Accounts</h2></div>
            <form className="admin-account-search" onSubmit={(event) => { event.preventDefault(); loadAccounts(query); }}>
              <input className="admin-quality-input" type="search" placeholder="Search email or ID" value={query} onChange={(event) => setQuery(event.target.value)} />
              <button className="admin-quality-btn-secondary" type="submit">Search</button>
            </form>
          </div>
          {revokeFeedback.message && (
            <div
              ref={revokeFeedbackRef}
              className={revokeFeedback.type === 'success'
                ? 'admin-quality-success'
                : revokeFeedback.type === 'cancelled'
                  ? 'admin-account-cancelled'
                  : 'admin-quality-error'}
              role={revokeFeedback.type === 'error' ? 'alert' : 'status'}
              tabIndex="-1"
            >
              {revokeFeedback.message}
              {revokeFeedback.requiresReauthentication && (
                <div className="admin-account-feedback-action">
                  <button className="admin-quality-btn-secondary" type="button" onClick={reauthenticateAdministrator}>Sign In Again</button>
                </div>
              )}
            </div>
          )}
          {suspensionFeedback.message && (
            <div
              ref={suspensionFeedbackRef}
              className={suspensionFeedback.type === 'success'
                ? 'admin-quality-success'
                : suspensionFeedback.type === 'cancelled'
                  ? 'admin-account-cancelled'
                  : 'admin-quality-error'}
              role={suspensionFeedback.type === 'error' ? 'alert' : 'status'}
              tabIndex="-1"
            >
              {suspensionFeedback.message}
              {suspensionFeedback.requiresReauthentication && (
                <div className="admin-account-feedback-action">
                  <button className="admin-quality-btn-secondary" type="button" onClick={reauthenticateAdministrator}>Sign In Again</button>
                </div>
              )}
            </div>
          )}
          {roleFeedback.message && (
            <div
              ref={roleFeedbackRef}
              className={roleFeedback.type === 'success'
                ? 'admin-quality-success'
                : roleFeedback.type === 'cancelled'
                  ? 'admin-account-cancelled'
                  : 'admin-quality-error'}
              role={roleFeedback.type === 'error' ? 'alert' : 'status'}
              tabIndex="-1"
            >
              {roleFeedback.message}
              {roleFeedback.requiresReauthentication && (
                <div className="admin-account-feedback-action">
                  <button className="admin-quality-btn-secondary" type="button" onClick={reauthenticateAdministrator}>Sign In Again</button>
                </div>
              )}
            </div>
          )}
          {deleteFeedback.message && (
            <div
              ref={deleteFeedbackRef}
              className={deleteFeedback.type === 'success'
                ? 'admin-quality-success'
                : deleteFeedback.type === 'cancelled'
                  ? 'admin-account-cancelled'
                  : 'admin-quality-error'}
              role={deleteFeedback.type === 'error' ? 'alert' : 'status'}
              tabIndex="-1"
            >
              {deleteFeedback.message}
              {deleteFeedback.requiresReauthentication && (
                <div className="admin-account-feedback-action">
                  <button className="admin-quality-btn-secondary" type="button" onClick={reauthenticateAdministrator}>Sign In Again</button>
                </div>
              )}
            </div>
          )}
          {loading ? <div className="admin-quality-loading">Loading accounts…</div> : (
            <div className="admin-quality-table-wrapper">
              <table className="admin-quality-table admin-account-table">
                <thead><tr><th>Account</th><th>Role</th><th>Effective Plan</th><th>Access Source</th><th>Status</th><th>Complimentary Grant</th><th>Actions</th></tr></thead>
                <tbody>{accounts.map((account) => {
                  const isCurrentAdministrator = account.id === currentAdminId;
                  return (
                  <tr key={account.id}>
                    <td><strong>{account.email}</strong><small>ID {account.id} · Created {formatDate(account.created_at)}</small></td>
                    <td>{account.role}</td><td>{account.effective_plan}</td><td>{account.access_source}</td><td>{account.account_status}</td>
                    <td>{account.complimentary_grant ? <div className="admin-account-grant-detail">
                      <strong>{account.complimentary_grant.plan} · {formatDate(account.complimentary_grant.expires_at)}</strong>
                      <small>Granted by user {account.complimentary_grant.granted_by_user_id} on {formatDate(account.complimentary_grant.created_at)}</small>
                      <small>{account.complimentary_grant.reason}</small>
                    </div> : '—'}</td>
                    <td>{isCurrentAdministrator ? (
                      <span className="admin-account-current-user-note">Current administrator account</span>
                    ) : (
                      <div className="admin-account-actions">
                        {account.complimentary_grant && (
                          <button
                            className="admin-quality-btn-secondary"
                            onClick={(event) => requestRevokeConfirmation(account, event.currentTarget)}
                            disabled={revokeSubmitting && revokeConfirmation?.accountId === account.id}
                            aria-haspopup="dialog"
                          >
                            Revoke Grant
                          </button>
                        )}
                        {account.is_admin && (
                          <button
                            className="admin-quality-btn-secondary"
                            onClick={(event) => requestRoleConfirmation(account, event.currentTarget)}
                            disabled={roleSubmitting && roleConfirmation?.accountId === account.id}
                            aria-haspopup="dialog"
                          >
                            Remove Admin
                          </button>
                        )}
                        <button
                          className="admin-quality-btn-secondary"
                          onClick={(event) => requestSuspensionConfirmation(account, event.currentTarget)}
                          disabled={suspensionSubmitting && suspensionConfirmation?.accountId === account.id}
                          aria-haspopup="dialog"
                        >
                          {account.account_status === 'suspended' ? 'Reactivate' : 'Suspend'}
                        </button>
                        {account.email?.trim().toLowerCase() !== PROTECTED_DELETE_EMAIL && (
                          <button
                            className="admin-quality-btn-secondary"
                            onClick={(event) => requestDeleteConfirmation(account, event.currentTarget)}
                            disabled={deleteSubmitting && deleteConfirmation?.accountId === account.id}
                            aria-haspopup="dialog"
                          >
                            Delete permanently
                          </button>
                        )}
                      </div>
                    )}</td>
                  </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default AdminAccountManagement;
